import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  Paper
} from '@mui/material';
import { Close } from '@mui/icons-material';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { GalleryMediaItem, CMSViewMode } from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';
import { useTypography } from '../../../../context/TypographyContext';

interface CMSConsumerFeaturedGalleryProps {
  featuredCollection: any; // The featured collection data from CMS
  hasVideo?: boolean;
  invert?: boolean;
  marginTop?: number;
  marginBottom?: number;
  viewMode?: CMSViewMode;
  // Optional modal behavior
  useModal?: boolean;
  modalUrl?: string;
  catalogueUrl?: string;
  // Replace the text tile (top right on desktop, text block on mobile) with an image
  hideTextTile?: boolean;
}

const CMSConsumerFeaturedGallery: React.FC<CMSConsumerFeaturedGalleryProps> = ({
                                                                                 featuredCollection,
                                                                                 hasVideo = false,
                                                                                 invert = false,
                                                                                 marginTop = 5,
                                                                                 marginBottom = 5,
                                                                                 viewMode = 'live',
                                                                                 useModal = false,
                                                                                 modalUrl,
                                                                                 catalogueUrl,
                                                                                 hideTextTile = false
                                                                               }) => {
  const navigate = useNavigate();
  const { styleFor } = useTypography();
  const [modalOpen, setModalOpen] = useState(false);

  // Extract data from featured collection
  const title = featuredCollection?.page_title || '';
  const description = featuredCollection?.description || '';
  const images = featuredCollection?.gallery_media || [];
  const categoryId = featuredCollection?.category_id;

  // Find image by position
  const findImageByPosition = (position: string) => {
    const image = images.find((img: GalleryMediaItem) => img.position === position);
    return image?.file_link;
  };

  const mobileImages = hideTextTile
    ? ['top left', 'top right', 'bottom left', 'bottom right']
      .map((position) => ({ position, src: findImageByPosition(position) }))
      .filter((img) => !!img.src)
    : [{ position: 'bottom right', src: findImageByPosition('bottom right') }];

  // Handle click navigation
  const handleClick = async () => {
    if (useModal && modalUrl) {
      setModalOpen(true);
    } else if (catalogueUrl) {
      window.open(catalogueUrl, '_blank');
    } else if (categoryId) {
      try {
        const categoryResponse = await getCategoryDetails(categoryId, viewMode);
        if (categoryResponse.success && categoryResponse.cms_category) {
          const categoryUrl = buildCategoryUrl(categoryResponse.cms_category);
          navigate(categoryUrl);
        }
      } catch (error) {
        console.error('Error navigating to featured collection:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const showClickableCursor = (useModal && modalUrl) || catalogueUrl || categoryId;

  // Show missing content if no data
  if (!featuredCollection || !images || images.length === 0) {
    return <MissingContent sectionName="Featured Collection" />;
  }

  // Text content component
  const TextContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: '15px',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '10px 12px',
          background: '#EDEDED',
        }}
      >
        <Typography sx={styleFor('featured.label')}>
          Featured Collection
        </Typography>
      </Box>
      <Typography variant="h1" textAlign="center" sx={styleFor('featured.heading')}>
        {title}
      </Typography>
      <Typography textAlign="center" sx={styleFor('featured.description')}>
        {description}
      </Typography>
    </Box>
  );

  // Video content component
  const VideoContent = () => (
    <video
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
      autoPlay
      muted
      loop
    >
      <source src={''} type="video/mp4" />
    </video>
  );

  return (
    <>
      {/* Desktop View */}
      <Container
        maxWidth="lg"
        sx={{
          cursor: showClickableCursor ? 'pointer' : 'default',
          mt: marginTop,
          mb: marginBottom,
        }}
        onClick={handleClick}
      >
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <Grid container spacing={'16px'}>
            {/* Left Column */}
            <Grid item xs={6}>
              <Grid container spacing={'16px'}>
                {/* Top Left Image */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '8 / 9',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={findImageByPosition('top left')}
                      alt="featured-top-left"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Grid>

                {/* Bottom Left - Image or Text/Video */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '12 / 7',
                      overflow: 'hidden',
                    }}
                  >
                    {invert ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {hasVideo ? <VideoContent /> : <TextContent />}
                      </Box>
                    ) : (
                      <img
                        src={findImageByPosition('bottom left')}
                        alt="featured-bottom-left"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column */}
            <Grid item xs={6}>
              <Grid container spacing={'16px'}>
                {/* Top Right - Text/Video or Image */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '12 / 7',
                      overflow: 'hidden',
                    }}
                  >
                    {invert ? (
                      <img
                        src={findImageByPosition('bottom right')}
                        alt="featured-bottom-right"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : hideTextTile ? (
                      <img
                        src={findImageByPosition('top right')}
                        alt="featured-top-right"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {hasVideo ? <VideoContent /> : <TextContent />}
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Bottom Right Image */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '8 / 9',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={findImageByPosition('bottom right')}
                      alt="featured-bottom-right-main"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Mobile View */}
      <Box
        sx={{
          display: { xs: 'block', lg: 'none' },
          mt: marginTop,
          mb: marginBottom,
          width: '85%',
          mx: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', }}>
          {mobileImages.map((img) => (
            <img
              key={img.position}
              style={
                hideTextTile
                  ? { width: '100%', height: 'auto', display: 'block' }
                  : { width: '100%', aspectRatio: '3 / 2', objectFit: 'cover' }
              }
              src={img.src}
              alt={`featured-mobile-${img.position.replace(' ', '-')}`}
            />
          ))}
        </Box>

        {!hideTextTile && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              width: '100%',
              height: 'auto',
              pt: '1rem',
              gap: '15px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: '10px 12px',
                background: '#EDEDED',
              }}
            >
              <Typography sx={styleFor('featured.label')}>
                Featured Collection
              </Typography>
            </Box>
            <Typography variant="h5" sx={styleFor('featured.heading')}>
              {title}
            </Typography>
            <Typography sx={{ width: '100%', ...styleFor('featured.description') }}>
              {description}
            </Typography>
            <Button
              sx={{
                mt: 1.5,
                mb: 5,
                borderRadius: '0',
                padding: '1rem 3rem',
                backgroundColor: '#fff',
                ...styleFor('featured.button'),
              }}
              variant="outlined"
              color="primary"
              onClick={handleClick}
            >
              {useModal ? 'View Collection' : catalogueUrl ? 'View Catalogue' : 'Explore The Collection'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Modal for International Brands */}
      {useModal && (
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="featured-collection-modal"
          aria-describedby="featured-collection-viewer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper
            sx={{
              position: 'relative',
              width: '95vw',
              height: '90vh',
              maxWidth: '1200px',
              maxHeight: '800px',
              outline: 'none',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 24,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                },
              }}
            >
              <Close />
            </IconButton>

            {modalUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  paddingTop: 'max(60%, 326px)',
                }}
              >
                <iframe
                  allow="clipboard-write"
                  sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms"
                  allowFullScreen={true}
                  style={{
                    position: 'absolute',
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                  }}
                  src={modalUrl}
                  title={`Featured Collection - ${title}`}
                />
              </Box>
            )}
          </Paper>
        </Modal>
      )}
    </>
  );
};

export default CMSConsumerFeaturedGallery;